import React, { useRef, useEffect, useState } from 'react';
import Select, { components } from 'react-select';
import { injectIntl } from 'react-intl';
import { Title, Container, CustomStyles } from './styles';
import VirtualizedList from './virtualizedList';
import { Checkbox, Tooltip, Chip, Box } from '@material-ui/core';
import './styles.css';

type Props = {
  lineHeight: any,
  title: string,
  placeholder: string,
  error: any,
  isMulti: boolean,
  items: any,
  selectedItems: any,
  isDisabled: boolean,
  openTop: any,
  intl: any,
  autoCompleteCustomComponents: any,
  setSelectedItems: any,
  controlShouldRenderValue: boolean,
  customFilterFunction: any,
  useCheckbox: boolean,
  small: boolean,
  hasSelectAll: boolean,
}

type OptionValue = {
  data: {
    label: string;
    value: any;
    key: any;
  };
};

const CheckboxOption = (
  props: React.ComponentProps<typeof components.Option>
  & OptionValue
  & {
    selectedItems: any[],
    selectAll: boolean,
    selectAllLabel: string,
    small: boolean,
  }
) => {
  const { data, selectAll, selectAllLabel, small } = props;
  const { onMouseMove, onMouseOver, ...rest } = props.innerProps;
  const newProps = { ...props };
  // @ts-expect-error
  newProps.innerProps = rest;

  const [showTooltip, setShowTooltip] = useState(false);

  const child = useRef(null);

  useEffect(() => {
    setShowTooltip(!isEllipsisActive(child.current));
  }, [])

  const isEllipsisActive = (element) => {
    return element.offsetWidth < element.scrollWidth;
  }

  return (
    <components.Option {...newProps} className="default-custom-option">
      <Checkbox checked={selectAll && data.label === selectAllLabel} size={small ? 'small' : 'medium'} />
      {
        data?.email
          ? (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Tooltip title={data?.value} disableHoverListener={showTooltip}>
                <span className='option-text' ref={child}>{data?.name}</span>
              </Tooltip>
              <Tooltip title={data?.value} disableHoverListener={showTooltip}>
                <span className='secondary-option-text' ref={child}>{data?.email}</span>
              </Tooltip>
            </div>
          ) : (
            <Tooltip title={data.label || data?.value} disableHoverListener={showTooltip}>
              <span className='option-text' ref={child}>{data.label || data?.name}</span>
            </Tooltip>
          )
      }
    </components.Option>
  );
};

const DefaultOption = (props: React.ComponentProps<typeof components.Option> & OptionValue) => {
  const { data } = props;
  const { onMouseMove, onMouseOver, ...rest } = props.innerProps;
  const newProps = { ...props };
  // @ts-expect-error
  newProps.innerProps = rest;

  const [showTooltip, setShowTooltip] = useState(false);

  const child = useRef(null);

  useEffect(() => {
    setShowTooltip(!isEllipsisActive(child.current));
  }, [])

  const isEllipsisActive = (element) => {
    return element.offsetWidth < element.scrollWidth;
  }

  return (
    <components.Option {...newProps} className="default-custom-option">
      <Tooltip title={data.label} disableHoverListener={showTooltip}>
        <span className='option-text' ref={child}>{data.label}</span>
      </Tooltip>
    </components.Option>
  );
};

const NewSelectInput: React.FC<Props> = ({
  lineHeight = 30,
  title,
  placeholder,
  error,
  isMulti,
  items,
  selectedItems,
  isDisabled,
  openTop,
  intl,
  useCheckbox,
  small = false,
  autoCompleteCustomComponents = { Option: DefaultOption },
  setSelectedItems,
  controlShouldRenderValue = true,
  customFilterFunction,
  hasSelectAll = true,
}) => {
  const [selectAll, setSelectAll] = useState(false);
  const [search, setSearch] = useState('');

  const selectAllLabel = intl.messages['select_all.select_every_item'];
  const selectAllOption = { key: -1, value: -1, label: selectAllLabel };

  if (useCheckbox) autoCompleteCustomComponents.Option = (props) => (<CheckboxOption {...props} small={small} selectedItems={selectedItems} selectAll={selectAll} selectAllLabel={selectAllLabel} />);

  const titleComponent = title ? (
    <Title isMulti={isMulti} error={error}>
      {title}
    </Title>
  ) : null;

  const handleChange = (newValue) => {
    if (Array.isArray(newValue) && newValue.some(val => val.label === selectAllLabel) && !selectAll) {
      setSelectedItems(items.filter((item) => customFilterFunction(item, search)));
      setSelectAll(!search);
    } else if (Array.isArray(newValue) && newValue.some(val => val.label === selectAllLabel) && selectAll) {
      setSelectAll(false);
      setSelectedItems([]);
    } else {
      setSelectedItems(isMulti ? (newValue || []) : newValue);
    }
  };

  function isValidDataStructure(text) {
    try {
      const parsedTextToData = JSON.parse(text)

      if (!Array.isArray(parsedTextToData)) {
        return false;
      }

      for (const item of parsedTextToData) {
        if (
          typeof item !== 'object' ||
          !('value' in item) || typeof item.value !== 'number' ||
          !('label' in item) || typeof item.label !== 'string' ||
          !('key' in item) || typeof item.key !== 'number'
        ) {
          return false;
        }
      }
      return true;
    } catch (e) {
      console.log('e: ', e);
      return false;
    }
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();

    if(isValidDataStructure(event.clipboardData.getData('Text'))) {
      const pastedText = JSON.parse(event.clipboardData.getData('Text'));
      const newOptions = pastedText.map((chip, index) => ({
        label: chip.label,
        value: chip.value,
        key: chip.value
      }));
      setSelectedItems(newOptions);
    }
  };

  return (
    <>
      <Container>
        {titleComponent}
        <div onPaste={handlePaste}>
          <Select
            lineHeight={lineHeight}
            placeholder={placeholder}
            options={hasSelectAll ? [selectAllOption, ...items] : items}
            value={selectedItems}
            closeMenuOnSelect={!isMulti}
            isMulti={isMulti}
            isClearable={!isMulti}
            isDisabled={isDisabled}
            menuPlacement={openTop ? 'top' : 'bottom'}
            isSearchable
            noOptionsMessage={() => intl.messages['select_input.no_options']}
            styles={CustomStyles(true, error)}
            components={{ ...autoCompleteCustomComponents, MenuList: VirtualizedList }}
            onChange={handleChange}
            onInputChange={(value) => setSearch(value)}
            controlShouldRenderValue={controlShouldRenderValue}
            backspaceRemovesValue={false}
            filterOption={customFilterFunction}
          />
        </div>
      </Container>
    </>
  );
};

export default injectIntl(NewSelectInput);
